/**
 * Single Authoritative Business & Pricing Calculator for Flora Artisans Co. / Aster Decorz.
 * Used identically across serverless endpoints, database transactions, and UI previews.
 */

export interface PricingConfig {
  standardShippingFee: number;
  expressShippingFee: number;
  freeShippingThreshold: number;
  gstRate: number; // e.g. 0.18 for 18% GST
  pricesIncludeGst: boolean; // true = product prices are GST-inclusive (MRP), false = GST added on top
}

export const DEFAULT_PRICING_CONFIG: PricingConfig = {
  standardShippingFee: 60,
  expressShippingFee: 699,
  freeShippingThreshold: 5000,
  gstRate: 0.18,
  pricesIncludeGst: true,
};

export interface OrderItemLine {
  id?: string;
  price: number;
  qty: number;
  name?: string;
}

export interface CouponRule {
  code: string;
  discountType: string | "PERCENTAGE" | "FIXED" | "FLAT" | "PERCENT" | "AMOUNT";
  discountValue: number;
  maxDiscountCap?: number | null;
  minOrderAmount?: number | null;
  expiryDate?: string | null;
  status?: string;
}

export interface OrderCalculationResult {
  subtotal: number;
  discountAmount: number;
  taxableAmount: number;
  shippingFee: number;
  shippingMethod: "standard" | "express";
  gstAmount: number;
  pricesIncludeGst: boolean;
  finalTotal: number;
  amountInPaise: number;
  isFreeShipping: boolean;
  couponApplied: boolean;
  couponCode: string | null;
}

/**
 * Authoritatively calculates all line amounts, discounts, shipping tiers, GST, and final paise total.
 * Enforces integer arithmetic guarantees and strict rounding behavior.
 */
export function calculateOrderPricing(
  items: OrderItemLine[],
  options?: {
    shippingMethod?: "standard" | "express";
    coupon?: CouponRule | null;
    config?: Partial<PricingConfig>;
  }
): OrderCalculationResult {
  const config: PricingConfig = {
    standardShippingFee:
      typeof options?.config?.standardShippingFee === "number"
        ? options.config.standardShippingFee
        : DEFAULT_PRICING_CONFIG.standardShippingFee,
    expressShippingFee:
      typeof options?.config?.expressShippingFee === "number"
        ? options.config.expressShippingFee
        : DEFAULT_PRICING_CONFIG.expressShippingFee,
    freeShippingThreshold:
      typeof options?.config?.freeShippingThreshold === "number"
        ? options.config.freeShippingThreshold
        : DEFAULT_PRICING_CONFIG.freeShippingThreshold,
    gstRate:
      typeof options?.config?.gstRate === "number"
        ? options.config.gstRate
        : DEFAULT_PRICING_CONFIG.gstRate,
    pricesIncludeGst:
      typeof options?.config?.pricesIncludeGst === "boolean"
        ? options.config.pricesIncludeGst
        : DEFAULT_PRICING_CONFIG.pricesIncludeGst,
  };

  const shippingMethod = options?.shippingMethod === "express" ? "express" : "standard";

  // 1. Calculate Authoritative Subtotal
  let rawSubtotal = 0;
  if (Array.isArray(items)) {
    for (const item of items) {
      const unitPrice = Math.max(0, Number(item.price) || 0);
      const qty = Math.max(0, Math.floor(Number(item.qty) || 0));
      rawSubtotal += unitPrice * qty;
    }
  }
  const subtotal = Math.round(rawSubtotal);

  // 2. Coupon option removed: discount is always 0
  const discountAmount = 0;
  const couponApplied = false;
  const appliedCode: string | null = null;

  // 3. Taxable Base
  const taxableAmount = Math.max(0, subtotal - discountAmount);

  // 4. Shipping Calculation
  const isFreeShipping = subtotal >= config.freeShippingThreshold || subtotal === 0;
  let shippingFee = 0;

  if (!isFreeShipping) {
    shippingFee = shippingMethod === "express" ? config.expressShippingFee : config.standardShippingFee;
  } else if (shippingMethod === "express" && subtotal > 0) {
    // When subtotal qualifies for free standard, express charges only express differential or flat express
    shippingFee = config.expressShippingFee;
  }

  // 5. Statutory GST Calculation
  // When prices include GST (Indian MRP): gstAmount = round((taxableAmount * gstRate) / (1 + gstRate))
  // When prices exclude GST: gstAmount = round(taxableAmount * gstRate)
  const pricesIncludeGst = config.pricesIncludeGst ?? true;
  const gstAmount = pricesIncludeGst
    ? Math.round((taxableAmount * config.gstRate) / (1 + config.gstRate))
    : Math.round(taxableAmount * config.gstRate);

  // 6. Final Total & Razorpay Paise
  // When prices include GST: finalTotal = taxableAmount + shippingFee (GST is already in taxableAmount)
  // When prices exclude GST: finalTotal = taxableAmount + shippingFee + gstAmount
  const finalTotal = pricesIncludeGst
    ? Math.max(0, taxableAmount + shippingFee)
    : Math.max(0, taxableAmount + shippingFee + gstAmount);
  const amountInPaise = Math.round(finalTotal * 100);

  return {
    subtotal,
    discountAmount,
    taxableAmount,
    shippingFee,
    shippingMethod,
    gstAmount,
    pricesIncludeGst,
    finalTotal,
    amountInPaise,
    isFreeShipping,
    couponApplied,
    couponCode: appliedCode,
  };
}
